/**
 * Sincroniza nombre comercial con razón social del cliente fiscal.
 * Solo rellena automático si está vacío o aún coincide con la razón social anterior.
 */

/** Fragmentos de etiqueta CSF mal capturados como nombre (p. ej. «o razón», «FISCAL»). */
export function isJunkRazonSocial(value) {
  const v = String(value || "").trim();
  if (!v || v.length < 3) return true;
  if (/^(completo|nombre|denominaci[oó]n(?:\s+o)?|(?:o\s+)?raz[oó]n(?:\s+social)?|social|fiscal)$/i.test(v)) {
    return true;
  }
  if (/^o\s+raz/i.test(v)) return true;
  if (/valida\s+tu\s+informaci/i.test(v)) return true;
  if (/situaci[oó]n\s+fiscal|domicilio\s+fiscal|constancia\s+de\s+situaci/i.test(v)) return true;
  if (/lugar\s+y\s+fecha|fecha\s+de\s+emisi/i.test(v)) return true;
  if (/^(situaci[oó]n|domicilio|constancia|informaci[oó]n|valida|r[eé]gimen|lugar|fecha)$/i.test(v)) return true;
  return false;
}

export function defaultNombreComercialFromClient(client) {
  const saved = (client?.nombre_comercial || "").trim();
  if (saved && !isJunkRazonSocial(saved)) return saved;
  const rs = (client?.razon_social || "").trim();
  return isJunkRazonSocial(rs) ? "" : rs;
}

/** Nombre para listados: nunca muestra basura de etiqueta CSF. */
export function displayClientNombre(client) {
  const nc = String(client?.nombre_comercial || "").trim();
  if (nc && !isJunkRazonSocial(nc)) return nc;
  const rs = String(client?.razon_social || "").trim();
  if (rs && !isJunkRazonSocial(rs)) return rs;
  return "";
}

/** Razón social editable: vacía si el valor guardado es basura de CSF. */
export function sanitizeRazonSocialForForm(value) {
  const v = String(value || "").trim();
  return isJunkRazonSocial(v) ? "" : v;
}

export function mergeRazonSocialIntoForm(form, newRazonSocial) {
  const prevRazon = (form?.razon_social || "").trim();
  const nextRazon = String(newRazonSocial ?? "");
  const currentNc = (form?.nombre_comercial || "").trim();
  const next = { ...form, razon_social: nextRazon };
  if (!currentNc || currentNc === prevRazon || isJunkRazonSocial(currentNc)) {
    next.nombre_comercial = nextRazon;
  }
  return next;
}

export function mergeParsedClientFieldsIntoForm(form, fields = {}) {
  const fromPdf = String(fields.razon_social || "").trim();
  const current = String(form.razon_social || "").trim();
  const nextRazon =
    fromPdf && !isJunkRazonSocial(fromPdf)
      ? fromPdf
      : isJunkRazonSocial(current)
        ? fromPdf || current
        : fromPdf || current;
  let next = mergeRazonSocialIntoForm(form, nextRazon);
  const curp = String(fields.curp || "").trim().toUpperCase();
  const tipoFromPdf = String(fields.tipo_persona || "").trim().toLowerCase();
  next = {
    ...next,
    rfc: fields.rfc || next.rfc,
    regimen_fiscal: fields.regimen_fiscal || next.regimen_fiscal,
    codigo_postal: fields.codigo_postal || next.codigo_postal,
    direccion: fields.direccion || next.direccion,
    notas: fields.notas || next.notas,
  };
  const rfcLen = String(next.rfc || "").replace(/\s/g, "").length;
  // Longitud de RFC manda sobre tipo_persona del PDF (evita borrar CURP de PF).
  if (rfcLen === 13) {
    next.tipo_persona = "fisica";
  } else if (rfcLen === 12) {
    next.tipo_persona = "moral";
  } else if (tipoFromPdf === "fisica" || tipoFromPdf === "moral") {
    next.tipo_persona = tipoFromPdf;
  }
  if (curp && next.tipo_persona !== "moral") {
    next.curp = curp.slice(0, 18);
  } else if (next.tipo_persona === "moral" && rfcLen === 12) {
    next.curp = "";
  } else if (curp && rfcLen === 13) {
    next.curp = curp.slice(0, 18);
  }
  return next;
}
